import React, {useEffect, useEffectEvent, useRef, useState} from 'react';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import EmarsysZone from 'coreComponents/EmarsysZone';
import LivePreviewEntry from 'coreComponents/LivePreviewEntry';
import {useIsFirstRender, useLanguageDirection, useLocalesMgr, useRequestClass, useSetUI, useUI} from 'coreHooks';
import {timeout as delay} from 'coreUtils/utils';

import Button from 'brandComponents/Button';
import Icon from 'brandComponents/Icon';
import {TIMEOUT_ROTATIONAL_BANNER} from 'brandConstants';

import {IRotationalBannerProps, TRotationalBannerSlide} from './IRotationalBanner';

import styles from './RotationalBanner.module.scss';

const RotationalBanner = ({
	rotationalBannerData,
	livePreviewEntryId,
	classNames,
	timeToSwapText,
}: IRotationalBannerProps) => {
	const slides = rotationalBannerData ?? [];
	const changeTextEffectTimeout = 500;
	const isFirstRender = useIsFirstRender();
	const setUI = useSetUI();
	const rotationalBanner = useUI('rotationalBanner');
	const isRTL = useLanguageDirection().direction === 'rtl';
	const [isChanging, setIsChanging] = useState(false);
	const [isSliding, setIsSliding] = useState(false);
	const [index, setIndex] = useState(0);
	const localeMgr = useLocalesMgr();
	const currentLocale = localeMgr.getCurrentLocale();
	const prevLanguageRef = useRef(currentLocale.getLanguageCode());
	const scrollableContainer = useRef<HTMLDivElement>(null);
	const rotationalBannerTimeout = useRef<ReturnType<typeof setInterval> | null>(null);
	const changeEffectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const startSlidingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const slidingMessageEndTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const slidingAnimationID = useRef<number | null>(null);
	const [isManualClosed, setManualClose] = useState(false);
	const [text, setText] = useState<TRotationalBannerSlide | null>(slides[0] ?? null);
	const [prevCountry, setPrevCountry] = useState(currentLocale.getCountryCode());
	const requestClass = useRequestClass();
	const isLivePreview = requestClass.isLivePreview;

	const changeTextMessage = (selectedIndex: number) => {
		clearTimeout(changeEffectTimeout.current ?? undefined);
		clearTimeout(startSlidingTimeout.current ?? undefined);
		clearTimeout(slidingMessageEndTimeout.current ?? undefined);
		cancelAnimationFrame(slidingAnimationID.current ?? undefined);

		setIsSliding(false);
		setIsChanging(true);

		changeEffectTimeout.current = setTimeout(() => {
			if (scrollableContainer.current) {
				scrollableContainer.current.scrollTo(0, 0);
			}

			const normalizedIndex = selectedIndex === slides.length ? 0
				: selectedIndex < 0 ? slides.length - 1
					: selectedIndex;

			setIndex(normalizedIndex);
			setText(slides[normalizedIndex]);
			setIsChanging(false);
		}, changeTextEffectTimeout);
	};

	useEffect(() => {
		if (!isFirstRender) {
			setText(slides[0] ?? null);
		}
	}, [rotationalBannerData]);

	useEffect(() => {
		if (currentLocale.getLanguageCode() !== prevLanguageRef.current) {
			prevLanguageRef.current = currentLocale.getLanguageCode();
			setManualClose(false);
		}
	}, [currentLocale.getLanguageCode()]);

	useEffect(() => {
		if (currentLocale.getCountryCode() !== prevCountry) {
			setManualClose(false);
			setPrevCountry(currentLocale.getCountryCode());
		}
	}, [currentLocale.getCountryCode()]);

	// Each tick reads the current slide and state, the interval is not restarted when they change.
	const onSliderTick = useEffectEvent(() => {
		if (slides.length > 1 && !isSliding && !isChanging) {
			changeTextMessage(index + 1);
		}
	});

	useEffect(() => {
		if (slides.length > 0) {
			changeTextMessage(index);
			clearInterval(rotationalBannerTimeout.current ?? undefined);
			rotationalBannerTimeout.current = setInterval(() => onSliderTick(), timeToSwapText || TIMEOUT_ROTATIONAL_BANNER);
		} else {
			clearInterval(rotationalBannerTimeout.current ?? undefined);
			setText(null);
		}

		return () => clearInterval(rotationalBannerTimeout.current ?? undefined);
	}, [rotationalBannerData]);

	useEffect(() => {
		if (scrollableContainer.current && !isLivePreview && text) {
			const scrollablePart =
				(scrollableContainer.current.scrollWidth - scrollableContainer.current.offsetWidth - 1)
				* (isRTL ? -1 : 1);
			const shouldScroll = isRTL
				? scrollableContainer.current.scrollLeft > scrollablePart
				: scrollableContainer.current.scrollLeft < scrollablePart;

			if (shouldScroll) {
				setIsSliding(true);
				clearTimeout(startSlidingTimeout.current ?? undefined);

				const sliderStep = async () => {
					if (!scrollableContainer.current) {
						return;
					}

					const {scrollLeft} = scrollableContainer.current;
					const continueScrolling = isRTL
						? scrollLeft > scrollablePart
						: scrollLeft < scrollablePart;

					if (continueScrolling) {
						scrollableContainer.current.scrollBy(isRTL ? -1 : 1, 0);

						const isMobile = ViewportMgr.query('<=small');

						await delay(isMobile ? 25 : 0);
						slidingAnimationID.current = window.requestAnimationFrame(sliderStep);
					} else {
						clearTimeout(slidingMessageEndTimeout.current ?? undefined);
						slidingMessageEndTimeout.current = setTimeout(() => {
							if (slides.length === 1 && scrollableContainer.current) {
								const scrollStart = isRTL ? scrollableContainer.current.offsetWidth : 0;

								scrollableContainer.current.scrollTo({left: scrollStart, behavior: 'smooth'});
								setTimeout(() => setIsSliding(false), 750);
							} else {
								setIsSliding(false);
							}
						}, TIMEOUT_ROTATIONAL_BANNER);
						cancelAnimationFrame(slidingAnimationID.current ?? undefined);
					}
				};

				startSlidingTimeout.current = setTimeout(() => {
					slidingAnimationID.current = window.requestAnimationFrame(sliderStep);
				}, TIMEOUT_ROTATIONAL_BANNER);
			}
		}
	}, [text, isRTL, isSliding, isLivePreview, slides.length]);

	useEffect(() => {
		if (isManualClosed || !rotationalBannerData) {
			setUI.setRotationalBanner(false);
		} else if (rotationalBannerData && !isManualClosed) {
			setUI.setRotationalBanner(true);
		}
	}, [isManualClosed, rotationalBannerData]);

	return (
		<div
			className={cn(
				classNames?.wrap,
				styles['rotational-banner'],
				!rotationalBanner && [classNames?.hidden, styles['rotational-banner__hidden']],
			)}
		>
			<EmarsysZone id="top-banner" />
			<div className={cn(styles['rotational-banner__container'])}>
				{slides.length > 1 && (
					<Button
						variant="anything"
						classNames={cn(styles['rotational-banner__prev'], {
							[styles['rotational-banner__prev-changing']]: isChanging,
						})}
						onClick={() => {
							changeTextMessage(index - 1);
						}}
					>
						<Icon name="arrow-left" width={16} height={16} />
					</Button>
				)}
				<div
					className={cn(styles['rotational-banner__message--container'], {
						[styles['rotational-banner__message--container_changing']]: isChanging,
					})}
					ref={scrollableContainer}
				>
					<LivePreviewEntry entryId={livePreviewEntryId} fieldId="content">
						<div className={styles['rotational-banner__message']}>
							{text}
						</div>
					</LivePreviewEntry>
				</div>
				{slides.length > 1 && (
					<Button
						variant="anything"
						classNames={cn(styles['rotational-banner__next'], {
							[styles['rotational-banner__next-changing']]: isChanging,
						})}
						onClick={() => {
							changeTextMessage(index + 1);
						}}
					>
						<Icon name="arrow-right" width={16} height={16} />
					</Button>
				)}
			</div>
			<Button
				classNames={styles['rotational-banner__close']}
				variant="anything"
				onClick={() => setManualClose(true)}
			>
				<Icon name="close-menu" height={16} width={16}/>
			</Button>
		</div>
	);
};

export default RotationalBanner;
